import { Autocomplete, InputAdornment, TextField } from '@mui/material'
import React, { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { TState } from 'redux/config/root-types'
import useDebounce from 'ui/hooks/useDebounce'
import { Search } from 'lucide-react'
import { RequestMethods } from 'utils/axios/methods'
import { TLocation } from 'utils/types/TLocation'

type Props = {
  location: TLocation | null
  onChangeLocation: (location: TLocation | null) => void
}

export const LocationInput: FC<Props> = ({ location, onChangeLocation }) => {
  const dispatch = useDispatch()
  const filters = useSelector((state: TState) => state.bookings.filters)

  const [inputValue, setInputValue] = useState(filters.searchStr || '')
  // const [value, setValue] = useState<TLocation | null>(null)
  const [options, setOptions] = useState<TLocation[]>([])
  const [loading, setLoading] = useState(false)

  const debouncedInput = useDebounce(inputValue, 1000)

  useEffect(() => {
    if (debouncedInput.trim().length < 2) {
      setOptions([])
      setLoading(false)
      return
    }

    const fetchCities = async () => {
      setLoading(true)
      try {
        const results = await searchCities(debouncedInput)
        setOptions(results)
      } catch (error) {
        console.error('Failed to fetch cities:', error)
        setOptions([])
      } finally {
        setLoading(false)
      }
    }

    fetchCities()
  }, [debouncedInput])

  return (
    <Autocomplete
      freeSolo
      options={options}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.display_name)}
      value={location}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue)
      }}
      onChange={(_, newValue) => {
        if (typeof newValue === 'string') {
          onChangeLocation(null)
          setInputValue(newValue)
        } else if (newValue === null) {
          onChangeLocation(null)
          setInputValue('')
        } else {
          onChangeLocation(newValue)
          setInputValue(newValue.display_name)
        }
      }}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Location"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" sx={{ mt: 0, pl: '0.5rem' }}>
                <Search size={'1.25rem'} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: '100%',
            marginTop: 0,
            backgroundColor: '#FFF',
            borderRadius: 1,
            '& .MuiOutlinedInput-root': {
              height: '3rem',
              borderRadius: 1,
              display: 'flex',
              padding: 0,
              alignItems: 'center',
              '& fieldset': {
                borderColor: '#E5E7EB',
              },
              '&:hover fieldset': {
                borderColor: '#E5E7EB',
              },
              '&.Mui-focused fieldset': {
                borderWidth: 1,
                borderColor: '#E5E7EB',
              },
            },
            '& .MuiInputBase-input': {
              fontSize: '1.125rem',
              padding: '0 4px',
              lineHeight: 'normal',
            },
          }}
        />
      )}
    />
  )
}

const searchCities = async (query: string): Promise<TLocation[]> => {
  const { get } = RequestMethods()
  const res = await get(`/search/location?searchStr=${encodeURIComponent(query)}&language=en`)
  return res.data as TLocation[]
}
